
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth";
import { getBabyProfile, syncBabyProfile } from "@/utils/babyProfile";
import { 
  getFamilyProfile,
  getHeightUnitPreference,
  getWeightUnitPreference,
  syncFamilyProfile 
} from "@/utils/family";
import { calculateAgeInMonths } from "@/utils/ageCalculator";

export function useProfileData() {
  const { user } = useAuth();
  const [babyProfile, setBabyProfile] = useState<any>(null);
  const [familyProfile, setFamilyProfile] = useState<any>(null);
  const [useMetricHeight, setUseMetricHeight] = useState<boolean>(true);
  const [useMetricWeight, setUseMetricWeight] = useState<boolean>(true);
  const [profileRefreshKey, setProfileRefreshKey] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [babyAgeInMonths, setBabyAgeInMonths] = useState<number>(0);
  
  // Sync profiles when user logs in
  useEffect(() => {
    const syncProfiles = async () => {
      if (user?.id) {
        try {
          await syncBabyProfile();
          await syncFamilyProfile();
        } catch (error) {
          console.error("Error syncing profiles:", error);
        }
      }
    };
    
    syncProfiles();
  }, [user]);
  
  // Load data when user is available or profile refresh is triggered
  useEffect(() => {
    if (!user?.id) {
      setBabyProfile(null);
      setFamilyProfile(null);
      setUseMetricHeight(true);
      setUseMetricWeight(true);
      setBabyAgeInMonths(0);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    // Load profiles
    const loadProfiles = async () => {
      try {
        const [babyProfileData, familyProfileData] = await Promise.all([
          getBabyProfile(),
          getFamilyProfile()
        ]);
        
        setBabyProfile(babyProfileData);
        setFamilyProfile(familyProfileData);
        setUseMetricHeight(getHeightUnitPreference());
        setUseMetricWeight(getWeightUnitPreference());
        
        // Calculate baby's age in months
        if (babyProfileData?.birthday) {
          const birthday = new Date(babyProfileData.birthday);
          const ageInMonths = calculateAgeInMonths(birthday);
          setBabyAgeInMonths(ageInMonths);
          console.log("[useProfileData] Baby age in months:", ageInMonths);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading profiles:", error);
        setIsLoading(false);
      }
    };
    
    loadProfiles();
  }, [user, profileRefreshKey]);
  
  const refreshProfiles = async () => {
    if (user?.id) {
      try {
        const [babyProfileData, familyProfileData] = await Promise.all([
          getBabyProfile(),
          getFamilyProfile()
        ]);
        
        setBabyProfile(babyProfileData);
        setFamilyProfile(familyProfileData);
        setUseMetricHeight(getHeightUnitPreference());
        setUseMetricWeight(getWeightUnitPreference());
        
        // Recalculate baby's age in months
        if (babyProfileData?.birthday) {
          const birthday = new Date(babyProfileData.birthday);
          const ageInMonths = calculateAgeInMonths(birthday);
          setBabyAgeInMonths(ageInMonths);
        }
      } catch (error) {
        console.error("Error refreshing profiles:", error);
      }
      
      setProfileRefreshKey(prevKey => prevKey + 1);
    }
  };

  return {
    user,
    babyProfile,
    familyProfile,
    useMetricHeight,
    useMetricWeight,
    isLoading,
    babyAgeInMonths,
    refreshProfiles
  };
}
